import React, { useMemo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FavoriteButton from '../button/FavoriteButton';
import ShareButton from '../button/ShareButton';
import CountdownPreview from '../utils/CountDownPreview';
import Single from '../../assets/icone/card/iconaSingleFill.svg';
import Aereo from '../../assets/icone/card/iconaAereoFill.svg';
import { Link } from 'react-router-dom';
import AirlineLogo from '../../utils/AirlineLogo';
import { formatDate } from '../../utils/DateFormatter';

const FlightCard = React.memo(({ ticket }) => {
    const [showReturnFlight, setShowReturnFlight] = useState(false);
    const [isFlipped, setIsFlipped] = useState(false);
    const { t } = useTranslation();

    const handleFlip = () => {
        // Imposta subito lo stato dello switch
        setShowReturnFlight((prevState) => !prevState);

        // Aggiungi un ritardo prima di avviare il flip della card (es. 400ms per l'animazione dello switch)
        setTimeout(() => {
            setIsFlipped((prev) => !prev); // Inizia la rotazione della card dopo il delay
        }, 5000); // Ritardo di 400ms corrispondente alla durata dell'animazione dello switch
    };


    const handleFavoriteClick = useCallback((e) => {
        e.preventDefault();   // Blocca la navigazione
        e.stopPropagation();  // Ferma la propagazione del click
        // Logica per il pulsante Preferito
    }, []);
    const handleShareClick = useCallback((e) => {
        e.preventDefault();   // Blocca la navigazione
        e.stopPropagation();  // Ferma la propagazione del click
        // Logica per il pulsante Condividi
    }, []);

    const cardUrl = `${window.location.origin}/ticket/room/${ticket._id}`;

    const formatLuggage = useCallback((luggage, luggageReturn) => {
        const selectedLuggage = showReturnFlight ? luggageReturn : luggage;
        if (selectedLuggage && selectedLuggage.length > 0) {
            const totalBags = selectedLuggage.reduce((acc, item) => acc + item.quantity, 0);
            return `Default + ${totalBags}`;
        }
        return 'base';
    }, [showReturnFlight, ticket.luggage, ticket.luggageReturn]);

    const formatTime = (dateTime) => {
        const date = new Date(dateTime);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' });
    };

    const getFlightDuration = useCallback((departureTime, arrivalTime) => {
        const diffInMs = new Date(arrivalTime) - new Date(departureTime);
        const hours = Math.floor(diffInMs / (1000 * 60 * 60));
        const minutes = Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60));
        return `${hours}h ${minutes}m`;
    }, []);


    const getHighestOffer = useCallback((offers) => {
        if (!offers || offers.length === 0) return null;
        return Math.max(...offers.map(offer => offer.offer_price));
    }, [ticket.offers]);

    const calculateDiscountPercentage = useCallback((maxPrice, buyNowPrice) => {
        if (!maxPrice || !buyNowPrice || maxPrice <= buyNowPrice) return null;
        const discount = ((maxPrice - buyNowPrice) / maxPrice) * 100;
        return Math.floor(discount);
    }, [ticket.maxPrice, ticket.buyNowPrice]);


    const flightDepartureCity = showReturnFlight ? ticket.flightDepartureCityReturn : ticket.flightDepartureCity;
    const flightDestinationCity = showReturnFlight ? ticket.flightDestinationCityReturn : ticket.flightDestinationCity;
    const flightDateTimeDeparture = showReturnFlight ? ticket.flightDateTimeReturnDeparture : ticket.flightDateTimeDeparture;
    const flightDateTimeDestination = showReturnFlight ? ticket.flightDateTimeReturnDestination : ticket.flightDateTimeDestination;
    const flightClass = showReturnFlight ? ticket.flightClassReturn : ticket.flightClass;
    const numberFlight = showReturnFlight ? ticket.numberFlightReturn : ticket.numberFlight;

    const highestOffer = useMemo(() => getHighestOffer(ticket.offers), [ticket.offers]);
    const discountPercentage = useMemo(() => {
        return calculateDiscountPercentage(ticket.maxPrice, ticket.buyNowPrice);
    }, [ticket.maxPrice, ticket.buyNowPrice]);

    // Calcola il prezzo da mostrare considerando finalPrice (con coupon) o buyNowPrice (che ha già le penali incluse)
    const displayPrice = useMemo(() => {
        // Se esiste finalPrice (coupon applicato), usalo
        if (ticket.finalPrice !== undefined && ticket.finalPrice !== null) {
            return Math.floor(ticket.finalPrice);
        }
        // Altrimenti usa buyNowPrice (che ha già le penali incluse)
        return Math.floor(ticket.buyNowPrice || 0);
    }, [ticket.finalPrice, ticket.buyNowPrice]);

    const formattedDepartureTime = useMemo(() => formatTime(flightDateTimeDeparture), [flightDateTimeDeparture, showReturnFlight]);
    const formattedArrivalTime = useMemo(() => formatTime(flightDateTimeDestination), [flightDateTimeDestination, showReturnFlight]);
    const flightDuration = useMemo(() => getFlightDuration(flightDateTimeDeparture, flightDateTimeDestination), [flightDateTimeDeparture, flightDateTimeDestination, showReturnFlight]);

    const convertCurrencySymbol = useCallback((currency) => {
        switch (currency.toUpperCase()) {
            case 'EUR':
                return '€';
            case 'USD':
                return '$';
            case 'GBP':
                return '£';
            default:
                return currency;
        }
    }, []);


    const formatAirportName = (name) => {
        if (!name) return '';
        return name.length > 20 ? name.slice(0, 20) + '…' : name;
    };

    const getTextRotationStyle = (isFlipped) => {
        return {
            transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
            transition: 'transform 0.7s ease-in-out',
        };
    };

    return (
        <div className="perspective-1000  max-w-[350px]">
            <div className={`card-container bg-white rounded-2xl overflow-hidden w-full sm:relative p-4 ${showReturnFlight ? 'rotate-y-180' : ''}`}>
                <Link
                    to={`/ticket/flight/${ticket._id}`}
                    state={{ user: ticket.user_id }}
                >
                    {/* Contenuto principale */}
                    <div className="flex justify-between items-center mb-4" style={getTextRotationStyle(showReturnFlight)}>
                        <div className="flex-grow">
                            <CountdownPreview expiryDate={ticket.auctionEndTime} auctionStatus={ticket.auctionStatus} />
                        </div>
                        <div className="flex-grow-0 w-16"></div>
                        <div className="flex items-center space-x-2 z-20">
                            <div onClick={handleFavoriteClick}>
                                {/* Aggiungi l'handler onClick per fermare la propagazione */}
                                <FavoriteButton itemId={ticket._id} type="flights" />
                            </div>
                            <div onClick={handleShareClick}>
                                {/* Aggiungi l'handler onClick per fermare la propagazione */}
                                <ShareButton url={cardUrl} />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-between items-start" style={{ ...getTextRotationStyle(showReturnFlight), minWidth: '300px', maxWidth: '100%' }}>
                        <div className="text-lg flex flex-col items-center min-w-[100px]">
                            <div className="text-4xl font-heading font-semibold mb-2 min-w-[100px] text-center">
                                {flightDepartureCity.code}
                            </div>
                            <div className="text-sm text-burnt-coffee text-center max-w-[100px]">
                                {formatAirportName(flightDepartureCity.name)}
                            </div>
                        </div>
                        <div className="flex justify-center items-center w-20 h-28 pt-4">
                            <img
                                src={Aereo}
                                alt="Aereo"
                                className="w-20 h-20 transform -translate-y-4"
                                loading="lazy"
                            />
                        </div>
                        <div className="text-lg flex flex-col items-center min-w-[100px]">
                            <div className="text-4xl font-heading font-semibold mb-2 min-w-[100px] text-center">
                                {flightDestinationCity.code}
                            </div>
                            <div className="text-sm text-burnt-coffee text-center max-w-[100px]">
                                {formatAirportName(flightDestinationCity.name)}
                            </div>
                        </div>
                    </div>


                    <div className="grid grid-cols-3 gap-4 mt-4 mb-6" style={getTextRotationStyle(showReturnFlight)}>
                        {/* Prima Riga */}
                        <div className="text-sm flex flex-col items-center">
                            <div className="text-xl font-heading uppercase text-burnt-coffee font-semibold mb-1 text-left">
                                {t('flightFields.date')}
                            </div>
                            <div>
                            {formatDate(showReturnFlight ? ticket.flightDateTimeReturnDeparture : ticket.flightDateTimeDeparture)}

                            </div>
                        </div>

                        <div className="text-sm flex flex-col items-center">
                            <div className="text-xl font-heading uppercase text-burnt-coffee font-semibold mb-1 text-left">
                                {t('flightFields.bags')}
                            </div>
                            <div>
                                {formatLuggage(ticket.luggage, ticket.luggageReturn)}
                            </div>
                        </div>

                        <div className="text-sm flex flex-col items-center">
                            <div className="text-xl font-heading uppercase text-burnt-coffee font-semibold mb-1 text-left">
                                {t('flightFields.number')}
                            </div>
                            <div>
                                {numberFlight}
                            </div>
                        </div>

                        {/* Seconda Riga */}
                        <div className="text-sm flex flex-col items-center">
                            <div className="text-xl font-heading uppercase text-burnt-coffee font-semibold mb-1 text-left">
                                {t('travel.departure')}
                            </div>
                            <div className="text-center">
                                {formattedDepartureTime}
                            </div>
                        </div>

                        <div className="text-sm flex flex-col items-center">
                            <div className="text-xl font-heading uppercase text-burnt-coffee font-semibold mb-1 text-left">
                                {t('travel.duration')}
                            </div>
                            <div className="text-center">
                                {flightDuration}
                            </div>
                        </div>

                        <div className="text-sm flex flex-col items-center">
                            <div className="text-xl font-heading uppercase text-burnt-coffee font-semibold mb-1 text-left">
                                {t('travel.arrival')}
                            </div>
                            <div className="text-center">
                                {formattedArrivalTime}
                            </div>
                        </div>
                    </div>


                </Link>

                {/* Icona persone e A/R */}
                <div className="flex justify-between items-start mt-4 pb-4" style={getTextRotationStyle(showReturnFlight)}>
                    <div className="flex items-center">
                        <img src={Single} alt="Single Icon" className="w-8 h-8 mr-2" loading="lazy" />
                        <span className="text-lg font-semibold">{ticket.numberPeople}</span>
                    </div>
                    {ticket.flightDepartureCityReturn && (
                        <div className="flex items-center pl-8">
                            <label className="switch">
                                <input type="checkbox" checked={showReturnFlight} onChange={handleFlip} />
                                <span className="slider round">
                                    <span className="andata text-gray-300">{t('flightFields.gone')}</span>
                                    <span className="ritorno">{t('flightFields.return2')}</span>
                                </span>
                            </label>
                        </div>
                    )}

                    <div className="text-xl font-semibold uppercase font-heading text-center mt-1 pr-8">
                        {ticket.flightDepartureCityReturn ? "A/R" : t('sell.onlyGone')}
                    </div>
                </div>
            </div>

            {/* Prezzo e logo compagnia */}
            <div className="bg-white rounded-2xl border-t-2 border-gray-300 border-dashed overflow-hidden w-full ">
                <div className="flex justify-between items-center bg-white">
                    <div className="w-1/3">
                        <AirlineLogo companyName={ticket.flightCompanyName} />
                    </div>
                    <div className="w-1/3 text-center py-2">
                        <div className="flex justify-end">

                            <span className="text-xl font-bold text-burnt-coffee mr-4">
                                {convertCurrencySymbol(ticket.currency)}{highestOffer || displayPrice}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );


});

export default FlightCard;
